import api from '../../../shared/utils/api';
import { normalizarFiltroDeSegmento } from '../lib/filtroDeSegmento';
import type { SegmentFilterResult } from '../types/segments';

export interface MktSegment {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  /** v1 (os 4 campos de empresa) OU v2 (`{version: 2, kind, match, rules}`) —
   *  `lib/criteriosDeSegmento.lerCriterios` lê os dois. */
  criteria?: Record<string, unknown>;
  status: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // ── Segmentos v2 (W2, EF-2) — só chegam com `MKT_SEGMENTS_V2_ENABLED`;
  //    ausentes = ambiente sem v2. O BFF camelisa (`member_count` →
  //    `memberCount`); `services/mktSegmentsV2.service.normalizarSegmento`
  //    lê as duas grafias, por isso os tipos aqui são só os camel.
  kind?: 'dinamico' | 'estatico' | string;
  memberCount?: number | null;
  lastMaterializedAt?: string | null;
  materializationStatus?: 'never' | 'ok' | 'stale' | 'failed' | 'running' | string;
  materializationError?: string | null;
  memberCap?: number | null;
}

export interface MktSegmentCreatePayload {
  name: string;
  description?: string;
  criteria?: Record<string, unknown>;
  /** v2 — nomes pinados no contrato, em snake_case (o `toSnakeCase` do BFF
   *  não os altera; um BFF que só espelhe também os aceita). */
  kind?: 'dinamico' | 'estatico';
  member_cap?: number | null;
}

export interface MktSegmentUpdatePayload {
  name?: string;
  description?: string;
  criteria?: Record<string, unknown>;
  status?: string;
  kind?: 'dinamico' | 'estatico';
  member_cap?: number | null;
}

export interface MktDashboard {
  totalSegments: number;
  activeSegments: number;
  mrr: number;
  costPerLead: number;
  campaignRoi: number;
  weeklyLeads: Array<{ week: string; leads: number }>;
  funnel: {
    impressao: number;
    mql: number;
    sql: number;
    deal: number;
    won: number;
  };
}

export interface MktSegmentListResponse {
  items: MktSegment[];
  total: number;
  limit: number;
  offset: number;
}

// Campaigns
/** Full lifecycle (mirrors backend `CampaignStatus` — includes approval states). */
export type MktCampaignStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'active'
  | 'paused'
  | 'completed'
  | 'rejected';

export interface MktCampaign {
  id: string;
  tenantId: string;
  name: string;
  segmentId?: string;
  segmentName?: string;
  segmentIds?: string[];
  segmentNames?: string[];
  channel: string;
  channels?: string[];
  autopilot?: boolean;
  budget: number;
  startDate: string;
  endDate?: string;
  status: MktCampaignStatus;
  attributedDeals: number;
  leads: number;
  conversions: number;
  /** Rollup v2 (F1-4): spend REAL agregado de ChannelMetric (era = budget). */
  spend: number;
  revenue: number;
  /** Rollup v2 (F1-4): ROAS do backend (razão, ex. 3.2). null = sem spend → "—". */
  roas?: number | null;
  /**
   * Template Studio gap-close (janus-marketing migração 037) — o template
   * do janus-whatsapp vinculado ao conteúdo WhatsApp desta campanha,
   * declarado no seletor "Campanha" da `WhatsAppTemplatesTab` e persistido
   * no `onCreated` do Studio. DISTINTO dos templates `whatsapp_ads`
   * (CTWA, aprovação via Meta) que aparecem em `GET /:id/assets`.
   */
  whatsappTemplateId?: string | null;
  whatsappTemplateName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MktCampaignCreatePayload {
  name: string;
  segmentIds: string[];
  channels: string[];
  autopilot?: boolean;
  budget: number;
  startDate: string;
  endDate?: string;
}

/** PATCH payload — status transitions ride the same route (BFF `PATCH /:id`). */
export interface MktCampaignUpdatePayload extends Partial<MktCampaignCreatePayload> {
  status?: MktCampaignStatus | 'archived';
  /** Template Studio gap-close — see `MktCampaign`. `null` limpa o vínculo. */
  whatsappTemplateId?: string | null;
  whatsappTemplateName?: string | null;
}

export interface MktCampaignListResponse {
  items: MktCampaign[];
  total: number;
  limit: number;
  offset: number;
}

// ── Rollup v2 (F1-4 · RN-05) — performance shape ESTENDIDO (aditivo) ──
//
// Wire: snake_case do backend atravessa o `toCamelCase` do BFF. Os itens de
// `by_channel[]` já nascem literal-camelCase no backend (`ChannelBreakdownItem`
// — spendBrl/cplBrl), então o mapper é no-op neles.

export interface CampaignChannelBreakdownRow {
  /** null = bucket "Sem canal" (deals manuais sem cadeia — RN-05.3). */
  provider: string | null;
  spendBrl?: number | null;
  impressions?: number | null;
  clicks?: number | null;
  leads?: number | null;
  conversions?: number | null;
  cplBrl?: number | null;
  deals?: number | null;
  revenue?: number | null;
  roas?: number | null;
}

export interface CampaignSequenceEngagement {
  sent?: number | null;
  opened?: number | null;
  clicked?: number | null;
  openRate?: number | null;
  clickRate?: number | null;
}

export interface MktCampaignPerformance {
  // v1 (preservados byte-a-byte no wire — camelizados pelo BFF)
  campaignId: string;
  leadsCount: number;
  dealsCount: number;
  mrrAttributed: number;
  conversionRate: number;
  // v2 — novos (null-safe: derivado null quando denominador 0 → FE "—")
  wonDealsCount?: number | null;
  revenueAttributed?: number | null;
  spendRealBrl?: number | null;
  budgetBrl?: number | null;
  budgetUtilizationPct?: number | null;
  cplBrl?: number | null;
  roas?: number | null;
  impressions?: number | null;
  clicks?: number | null;
  sequenceEnrollments?: number | null;
  sequenceEngagement?: CampaignSequenceEngagement | null;
  byChannel?: CampaignChannelBreakdownRow[] | null;
  period?: { start?: string | null; end?: string | null } | null;
  /**
   * Marketing Hub v3 · W3, item 4a — presente só quando o pedido levou
   * `segment_id`. Com filtro aplicado, `spendRealBrl`/`cplBrl`/`roas`
   * viram `null` (verba não é atribuível a um segmento — RN do contrato);
   * a tela não deve mais somar/dividir esses campos como se o filtro não
   * existisse.
   */
  segment?: SegmentFilterResult | null;
}

// Content — two-axis model (Marketing Hub · Feature B).
//
// `type` (content_type) = WHAT the asset is (library taxonomy). The superset
// below includes the 6 values the FE already sent (which used to 422) plus the
// B2B additions — see 02-BDD-content-model / AC-35. `fileFormat` = the file
// format; `creativeFormat` = HOW an ad renders (channel-scoped, optional on the
// library asset). The BFF maps camelCase → snake_case (type→content_type,
// fileFormat→file_format, creativeFormat→creative_format, segmentId→segment_id).
export type MktContentType =
  | 'ebook'
  | 'whitepaper'
  | 'webinar'
  | 'infographic'
  | 'blog'
  | 'case_study'
  | 'report'
  | 'guide'
  | 'datasheet'
  | 'presentation'
  | 'video'
  | 'image'
  | 'template'
  | 'checklist'
  | 'other';

export type MktFileFormat = 'pdf' | 'pptx' | 'docx' | 'mp4' | 'png' | 'jpg' | 'link';

export const CONTENT_TYPE_OPTIONS: Array<{ value: MktContentType; label: string }> = [
  { value: 'ebook', label: 'E-book' },
  { value: 'whitepaper', label: 'Whitepaper' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'infographic', label: 'Infográfico' },
  { value: 'blog', label: 'Blog Post' },
  { value: 'case_study', label: 'Estudo de caso' },
  { value: 'report', label: 'Relatório' },
  { value: 'guide', label: 'Guia' },
  { value: 'datasheet', label: 'Datasheet' },
  { value: 'presentation', label: 'Apresentação' },
  { value: 'video', label: 'Vídeo' },
  { value: 'image', label: 'Imagem' },
  { value: 'template', label: 'Template' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'other', label: 'Outro' },
];

export const FILE_FORMAT_OPTIONS: Array<{ value: MktFileFormat; label: string }> = [
  { value: 'pdf', label: 'PDF' },
  { value: 'pptx', label: 'PPTX' },
  { value: 'docx', label: 'DOCX' },
  { value: 'mp4', label: 'MP4' },
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'link', label: 'Link' },
];

/**
 * `creative_format` is channel-scoped at publish/creative time; on the library
 * asset it is an OPTIONAL hint. We expose the union with the channel noted in
 * the label so the operator understands where each format applies.
 */
export const CREATIVE_FORMAT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'single_image', label: 'Imagem única (Meta/LinkedIn)' },
  { value: 'single_video', label: 'Vídeo único (Meta)' },
  { value: 'carousel', label: 'Carrossel (Meta/LinkedIn)' },
  { value: 'collection', label: 'Coleção (Meta)' },
  { value: 'lead_form', label: 'Lead form (Meta/LinkedIn)' },
  { value: 'rsa', label: 'RSA (Google)' },
  { value: 'image', label: 'Imagem (Google)' },
  { value: 'video', label: 'Vídeo (Google/LinkedIn)' },
  { value: 'youtube', label: 'YouTube (Google)' },
  { value: 'document', label: 'Document Ad (LinkedIn)' },
  { value: 'conversation', label: 'Conversation (LinkedIn)' },
];

export interface MktContent {
  id: string;
  tenantId: string;
  title: string;
  type: MktContentType;
  fileFormat?: MktFileFormat | null;
  creativeFormat?: string | null;
  segmentId?: string;
  segmentName?: string;
  /** F1-6 — vínculo opcional com a campanha guarda-chuva (coluna nova). */
  mktCampaignId?: string | null;
  description?: string;
  fileUrl?: string;
  fileName?: string;
  downloads: number;
  createdAt: string;
  updatedAt: string;
}

export interface MktContentCreatePayload {
  title: string;
  type: string;
  fileFormat?: string;
  creativeFormat?: string;
  segmentId?: string;
  /** F1-6 (RN-06.4) — vínculo opcional "Campanha" no upload/edição. */
  mktCampaignId?: string | null;
  /**
   * Snake twin of `mktCampaignId` — the BFF content mapper whitelists request
   * fields accepting `camel ?? snake` per key; sending both guarantees the
   * link flows regardless of which alias the proxy adopts (F1-6).
   */
  mkt_campaign_id?: string | null;
  description?: string;
}

export interface MktContentListResponse {
  items: MktContent[];
  total: number;
  limit: number;
  offset: number;
}

// A/B Testing
export interface AbTestVariant {
  id: string;
  name: string;
  splitPercent: number;
  impressions: number;
  conversions: number;
  conversionRate: number;
}

export interface AbTest {
  id: string;
  tenantId: string;
  name: string;
  campaignId?: string;
  status: 'draft' | 'running' | 'completed';
  variants: AbTestVariant[];
  winnerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AbTestCreatePayload {
  name: string;
  campaignId?: string;
  variants: Array<{ name: string; splitPercent: number }>;
}

export interface AbTestListResponse {
  items: AbTest[];
  total: number;
  limit: number;
  offset: number;
}

export const mktApi = {
  getDashboard: async (): Promise<MktDashboard> => {
    const { data } = await api.get<MktDashboard>('/mkt/dashboard');
    return data;
  },

  listSegments: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<MktSegmentListResponse> => {
    const { data } = await api.get<MktSegmentListResponse>('/mkt/segments', {
      params,
    });
    return data;
  },

  createSegment: async (payload: MktSegmentCreatePayload): Promise<MktSegment> => {
    const { data } = await api.post<MktSegment>('/mkt/segments', payload);
    return data;
  },

  updateSegment: async (id: string, payload: MktSegmentUpdatePayload): Promise<MktSegment> => {
    const { data } = await api.put<MktSegment>(`/mkt/segments/${id}`, payload);
    return data;
  },

  archiveSegment: async (id: string): Promise<void> => {
    await api.delete(`/mkt/segments/${id}`);
  },

  deleteSegment: async (id: string): Promise<void> => {
    await api.delete(`/mkt/segments/${id}`);
  },

  // Campaigns
  listCampaigns: async (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<MktCampaignListResponse> => {
    const { data } = await api.get<MktCampaignListResponse>('/mkt/campaigns', { params });
    return data;
  },

  getCampaign: async (id: string): Promise<MktCampaign> => {
    const { data } = await api.get<MktCampaign>(`/mkt/campaigns/${id}`);
    return data;
  },

  /**
   * GET /mkt/campaigns/:id/performance — rollup v2 (spend real + ROAS,
   * F1-4). `segmentId` opcional (item 4a, W3): filtra leads/inscrições
   * pelo segmento e devolve o bloco `segment` — normalizado à parte
   * (dual grafia; o restante do payload segue o cast direto de sempre).
   */
  getCampaignPerformance: async (
    id: string,
    segmentId?: string | null,
  ): Promise<MktCampaignPerformance> => {
    const { data } = await api.get<MktCampaignPerformance>(`/mkt/campaigns/${id}/performance`, {
      params: segmentId ? { segment_id: segmentId } : undefined,
    });
    const raw = data as unknown as Record<string, unknown>;
    return { ...data, segment: normalizarFiltroDeSegmento(raw.segment) };
  },

  createCampaign: async (payload: MktCampaignCreatePayload): Promise<MktCampaign> => {
    const { data } = await api.post<MktCampaign>('/mkt/campaigns', payload);
    return data;
  },

  updateCampaign: async (id: string, payload: MktCampaignUpdatePayload): Promise<MktCampaign> => {
    const { data } = await api.patch<MktCampaign>(`/mkt/campaigns/${id}`, payload);
    return data;
  },

  deleteCampaign: async (id: string): Promise<void> => {
    await api.delete(`/mkt/campaigns/${id}`);
  },

  /** POST /mkt/campaigns/:id/deals/:dealId — atribuição manual (existente). */
  attributeDeal: async (campaignId: string, dealId: string): Promise<void> => {
    await api.post(`/mkt/campaigns/${campaignId}/deals/${dealId}`);
  },

  // Content
  listContent: async (params?: {
    limit?: number;
    offset?: number;
    /** BFF query param is `type` (content type), forwarded as-is. */
    type?: MktContentType;
  }): Promise<MktContentListResponse> => {
    const { data } = await api.get<MktContentListResponse>('/mkt/content', { params });
    return data;
  },

  createContent: async (payload: MktContentCreatePayload): Promise<MktContent> => {
    const { data } = await api.post<MktContent>('/mkt/content', payload);
    return data;
  },

  uploadContentFile: async (id: string, file: File): Promise<MktContent> => {
    const formData = new FormData();
    formData.append('file', file);
    // Upload is a separate multipart step (AC-39). `__skipErrorNotification` so
    // a 413 (>50MB) is surfaced by the caller with a precise message instead of
    // the generic global toast.
    const { data } = await api.post<MktContent>(`/mkt/content/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      __skipErrorNotification: true,
    } as never);
    return data;
  },

  deleteContent: async (id: string): Promise<void> => {
    await api.delete(`/mkt/content/${id}`);
  },

  /**
   * Fetches an attached asset's raw bytes through the authenticated proxy
   * (`GET /mkt/content/:id/download`) — the library's bucket is private, so
   * a plain `<img src>`/direct GCS URL is never an option (see the BFF
   * route's own comment). Used when a consumer OUTSIDE this module's own
   * pages needs the actual file, not just its metadata — e.g. the WhatsApp
   * Template Studio re-hosting a picked library asset as template header
   * media (`whatsappApi.uploadTemplateMedia`).
   */
  downloadContentBlob: async (id: string): Promise<Blob> => {
    const { data } = await api.get<Blob>(`/mkt/content/${id}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // A/B Testing
  listAbTests: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<AbTestListResponse> => {
    const { data } = await api.get<AbTestListResponse>('/mkt/ab-tests', { params });
    return data;
  },

  createAbTest: async (payload: AbTestCreatePayload): Promise<AbTest> => {
    const { data } = await api.post<AbTest>('/mkt/ab-tests', payload);
    return data;
  },

  deleteAbTest: async (id: string): Promise<void> => {
    await api.delete(`/mkt/ab-tests/${id}`);
  },
};
